import { useState } from 'react';
import { EntityModal } from './EntityModal';

interface JsonViewerModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  data: unknown;
}

function renderValue(value: unknown, depth: number = 0): JSX.Element {
  if (value === null || value === undefined) {
    return <span className="text-gray-400 italic">null</span>;
  }

  if (typeof value === 'boolean') {
    return (
      <span className={value ? 'text-green-600' : 'text-red-600'}>{value ? 'Yes' : 'No'}</span>
    );
  }

  if (typeof value === 'number') {
    return <span className="text-blue-600 font-mono">{value}</span>;
  }

  if (typeof value === 'string') {
    // Check if it looks like a date
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      try {
        const d = new Date(value);
        if (!isNaN(d.getTime())) {
          return (
            <span className="text-purple-600">
              {d.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          );
        }
      } catch {
        // not a date
      }
    }
    return <span className="text-gray-900">{value}</span>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-gray-400 italic">empty list</span>;
    }
    return (
      <div className={depth > 0 ? 'ml-4 border-l-2 border-gray-100 pl-3' : ''}>
        {value.map((item, i) => (
          <div key={i} className="py-1">
            <span className="text-gray-400 text-xs mr-2">{i + 1}.</span>
            {renderValue(item, depth + 1)}
          </div>
        ))}
      </div>
    );
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) {
      return <span className="text-gray-400 italic">empty</span>;
    }
    return (
      <div className={depth > 0 ? 'ml-4 border-l-2 border-gray-100 pl-3' : ''}>
        {entries.map(([key, val]) => (
          <div key={key} className="py-1.5 flex flex-wrap gap-x-2">
            <span className="text-gray-500 text-sm font-medium min-w-[120px]">
              {formatKey(key)}
            </span>
            <div className="text-sm">{renderValue(val, depth + 1)}</div>
          </div>
        ))}
      </div>
    );
  }

  return <span className="text-gray-700">{String(value)}</span>;
}

function formatKey(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim();
}

export function JsonViewerModal({ open, onClose, title, data }: JsonViewerModalProps) {
  return (
    <EntityModal open={open} onClose={onClose} title={title} size="md">
      <div className="text-sm">{renderValue(data)}</div>
    </EntityModal>
  );
}

/** Inline link that shows item count and opens JsonViewerModal on click */
export function JsonPreviewLink({ data, label }: { data: unknown; label?: string }) {
  const [open, setOpen] = useState(false);

  const count = Array.isArray(data)
    ? data.length
    : typeof data === 'object' && data !== null
      ? Object.keys(data).length
      : 0;

  const displayLabel = label || (count === 1 ? '1 item' : `${count} items`);

  return (
    <>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className="text-primary-600 hover:text-primary-700 text-xs underline underline-offset-2"
      >
        {displayLabel}
      </button>
      <JsonViewerModal open={open} onClose={() => setOpen(false)} title="Details" data={data} />
    </>
  );
}
